import * as React from 'react'

import { cn } from '@/lib/utils'
import type { SortDirection } from '@tanstack/react-table'
import { FAIcon } from '../fa-icon'

type TableProps = React.ComponentProps<'table'> & {
  /** Classes for the horizontally scrolling container wrapping the table. */
  containerClassName?: string
}

function Table({ className, containerClassName, ...props }: TableProps) {
  return (
    <div
      data-slot="table-container"
      className={cn('relative w-full overflow-x-auto', containerClassName)}
    >
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-md', className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        "before:content-[''] before:table-row before:h-4",
        '[&_tr]:bg-quaternary',
        className
      )}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn(
        '[&_tr:last-child]:border-0 [&_tr]:border-b [&_tr]:border-neutral-200',
        className
      )}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'bg-muted/50 border-t font-medium [&>tr]:last:border-b-0',
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'data-[state=selected]:bg-muted transition-colors',
        className
      )}
      {...props}
    />
  )
}

type TableHeadProps = React.ComponentProps<'th'> & {
  center?: boolean
  end?: boolean
  sortable?: boolean
  sortDir?: false | SortDirection
}

function TableHead({
  children,
  className,

  center = false,
  end = false,
  sortable = false,
  sortDir = false,

  onClick,
  ...props
}: TableHeadProps) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'h-10 px-5 pt-5 pb-4',
        'text-foreground text-left uppercase align-middle font-semibold whitespace-nowrap',
        'first-of-type:rounded-l-xl last-of-type:rounded-r-xl',
        '[&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5',
        center && 'text-center',
        end && 'text-end',
        className
      )}
      {...props}
    >
      <span
        className={cn(
          'inline-flex items-center',
          sortable && 'cursor-pointer hover:text-secondary'
        )}
        onClick={onClick}
      >
        {children}

        {!!sortDir && (
          <FAIcon
            icon="triangle"
            type="solid"
            className={cn(
              'text-xs -translate-y-0.5 ml-2',
              sortDir === 'asc' && 'rotate-180'
            )}
          />
        )}
      </span>
    </th>
  )
}

interface TableCellProps extends React.ComponentProps<'td'> {
  center?: boolean
  end?: boolean
  narrow?: boolean
}

function TableCell({
  className,
  center = false,
  end = false,
  narrow = false,
  ...props
}: TableCellProps) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'px-4 py-3',
        'font-medium align-middle whitespace-nowrap',
        '[&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5',
        center && 'text-center',
        end && 'text-end',
        narrow && 'w-1',
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('text-muted-foreground mt-4 text-sm', className)}
      {...props}
    />
  )
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
}
